import { serverNow } from "@/filters/DateAndTime";
import dayjs from "@/plugins/dayjsExtensions";
import { TimeUnit } from "chart.js";
import _ from "lodash";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { DATE_FORMAT } from "../util";
import { TimePoint } from "./ChartUtils";

const MINUTE_TIME_RANGE = 3;
const HOUR_TIME_RANGE = 24;
const ENTIRE_CASE_ROUND_MIN_TO_DAY = 24;
const ROUND_UP_TO_NEXT_FIVE_MINUTE = 2;
const ROUND_UP_TO_NEXT_THIRTY_MINUTE = 3;
const ROUND_UP_TO_NEXT_HOUR = 24;

const SMALL_SCREEN_WIDTH = 500;
const SMALL_SCREEN_NUMBER_OF_TICKS = 2;
const DEFAULT_SCREEN_NUMBER_OF_TICKS = 4;
@Component({})
export default class ChartWithRange extends Vue {
  @Prop(String)
  type!: string;

  @Prop(Array)
  data!: Array<any>;

  @Prop(Object)
  library!: any;

  @Prop(Number)
  range!: number;

  @Prop(Number)
  height!: number;

  @Prop(Number)
  min!: number;

  @Prop(Number)
  max!: number;

  @Prop(Number)
  suggestedMin!: number;

  @Prop(Number)
  suggestedMax!: number;

  initialized = false;

  smallScreen() {
    return window.innerWidth < SMALL_SCREEN_WIDTH;
  }

  get filteredData() {
    // Show all points
    if (this.range === 0) {
      return this.data;
    }
    const edgeOfGraph = this.roundMaxToCleanNumber(+this.range);

    return this.data.map(data => {
      // clone to avoid updating data sent from server
      const clone = _.cloneDeep(data);
      // filter out points that will not show up on the glaph
      clone.data = data.data.filter((point: { x: dayjs.Dayjs; y: number }) => {
        const diff = edgeOfGraph.diff(point.x, "hour", true);
        return diff <= this.range;
      });

      // If some points were filtered, include the first point that was not in range
      // so that the graph draws a line off the edge of the graph towards it
      if (clone.data.length < data.data.length) {
        clone.data.unshift(data.data[data.data.length - clone.data.length - 1]);
      }
      return clone;
    });
  }

  get minX(): dayjs.Dayjs | null {
    let min: dayjs.Dayjs | null = null;
    this.data.forEach(d => {
      if (d.data.length) {
        const first = d.data[0] as TimePoint;
        if (min === null || +first.x < +min) {
          min = first.x;
        }
      }
    });

    // use serverNow as the min, this should only happen when there are no points
    if (min === null) {
      min = serverNow();
    }
    return min;
  }

  @Watch("data")
  @Watch("range")
  onRangeChange() {
    const xAxes = [];
    const minX = this.minX;
    // ensure it is a number
    const range = +this.range;
    const entireCaseRange = minX ? serverNow().diff(minX, "hour") : 0;

    const effectiveRange = range || entireCaseRange;
    // If range is Entire Case / Visit, force it to round up to the nearest hour
    const forceCleanHour = range === 0;
    const roundedMax = this.roundMaxToCleanNumber(
      effectiveRange,
      forceCleanHour
    );

    let maxTicksLimit = this.smallScreen()
      ? SMALL_SCREEN_NUMBER_OF_TICKS
      : DEFAULT_SCREEN_NUMBER_OF_TICKS;

    let maxRotation = 0;
    let unit: TimeUnit;
    if (effectiveRange < MINUTE_TIME_RANGE) {
      unit = "minute";
    } else if (effectiveRange <= HOUR_TIME_RANGE) {
      unit = "hour";
    } else {
      unit = "day";
      // Allow day graphs to show more day tick marks and fit on screen
      if (maxTicksLimit !== SMALL_SCREEN_NUMBER_OF_TICKS) {
        maxTicksLimit = 11;
        maxRotation = 90;
      }
    }
    const xAxesOne: any = {
      type: "time",
      time: {
        unit,
        displayFormats: {
          day: "M-D"
        }
      },
      ticks: {
        maxRotation,
        autoSkip: true,
        max: roundedMax.format(DATE_FORMAT),
        maxTicksLimit
      }
    };

    if (range !== 0 && range) {
      xAxesOne.ticks.min = roundedMax
        .subtract(range, "hour")
        .format(DATE_FORMAT);
    } else {
      xAxesOne.ticks.min = this.roundMinDownToCleanNumber(minX, effectiveRange);
    }
    xAxes.push(xAxesOne);
    if (this.chartConfig.scales.xAxes) {
      _.merge(this.chartConfig.scales.xAxes, xAxes);
    } else {
      this.chartConfig.scales.xAxes = xAxes;
    }

    this.chartConfig = { ...this.chartConfig };
  }

  private roundMinDownToCleanNumber(
    min: dayjs.Dayjs | null,
    effectiveRange: number
  ) {
    if (!min) {
      return null;
    }

    let minDate = min
      .millisecond(0)
      .second(0)
      .minute(0);
    if (effectiveRange > ENTIRE_CASE_ROUND_MIN_TO_DAY) {
      minDate = minDate.hour(0);
    }
    return minDate.format(DATE_FORMAT);
  }

  // Round forward to the closest 5-30 min / hour / day
  private roundMaxToCleanNumber(range: number, forceCleanHour = false) {
    const start = serverNow();
    let rounded = start
      .millisecond(0)
      .second(0)
      .minute(0);

    if (range < ROUND_UP_TO_NEXT_FIVE_MINUTE && !forceCleanHour) {
      const currentMinute = start.minute();
      const adding = (Math.floor(currentMinute / 5) + 1) * 5;
      rounded = rounded.add(adding, "minute");
    } else if (range < ROUND_UP_TO_NEXT_THIRTY_MINUTE && !forceCleanHour) {
      const currentMinute = start.minute();
      const adding = (Math.floor(currentMinute / 30) + 1) * 30;
      rounded = rounded.add(adding, "minute");
    } else if (range <= ROUND_UP_TO_NEXT_HOUR) {
      const currentHour = start.hour();
      rounded = rounded.hour(0).add(currentHour + 1, "hour");
    } else {
      rounded = rounded
        .hour(0)
        .day(0)
        .add(start.day() + 1, "day");
    }
    return rounded;
  }

  @Watch("library")
  onLibraryChange() {
    if (this.library) {
      _.merge(this.chartConfig, this.library);
    }
    this.onRangeChange();
  }

  mounted() {
    this.onLibraryChange();
    this.initialized = true;
  }

  chartConfig: any = {
    spanGaps: true,
    scales: {}
  };
}
